import { getApiClient } from "@src/api";
import { planQueryKeys } from "@src/components/plan/queries/query-keys";
import { graphql } from "@src/gql/gql";
import { captureRewardClaimed } from "@src/utils/analytics";
import { handleError } from "@src/utils/error-handling";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { referralProfileQueryKeys } from "../queries/get-my-referral-profile";

const claimReferralRewardMutation = graphql(/* GraphQL */ `
	mutation ClaimReferralReward($rewardCode: String!) {
		referrals {
			redeemReward(rewardCode: $rewardCode) {
				globalPosition
				needPaymentMethod
				profile {
					rewards {
						grantedRewards {
							campaignCode
							expiresAt
							isRedeemed
							refereeId
							rewardCode
						}
					}
				}
				stripe {
					renewsAt
					status
					subscriptionId
					needPaymentMethod
				}
			}
		}
	}
`);

export type RedeemRewardParams = {
	rewardCode: string;
};

/**
 * Mutation function for redeeming a referral reward for existing subscribers.
 * This applies the reward/discount to an existing subscription.
 */
export const claimReferralRewardMutationFn = async (
	params: RedeemRewardParams,
) => {
	const client = getApiClient();
	const result = await client.mutate({
		mutation: claimReferralRewardMutation,
		variables: {
			rewardCode: params.rewardCode,
		},
	});

	return result.data?.referrals.redeemReward;
};

export type RedeemRewardMutationResult = Awaited<
	ReturnType<typeof claimReferralRewardMutationFn>
>;

export const useRedeemReward = (
	onSuccess?: (result: RedeemRewardMutationResult) => void | Promise<void>,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: claimReferralRewardMutationFn,
		onError: (error) => {
			handleError(error, "Failed to redeem reward");
		},
		onSuccess: async (result, variables) => {
			captureRewardClaimed(variables.rewardCode);
			if (result && !result.needPaymentMethod) {
				await queryClient.invalidateQueries({
					queryKey: referralProfileQueryKeys.all,
				});
				await queryClient.invalidateQueries({
					queryKey: planQueryKeys.myPlan(),
				});
			}
			await onSuccess?.(result);
		},
	});
};
